import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStyles from "./style";
import {
  Box,
  Button,
  Chip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { CheckBox, Fingerprint, NotInterested } from "@mui/icons-material";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import NoteAddOutlined from "@mui/icons-material/NoteAddOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import moment from "moment";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDispatch } from "react-redux";

import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import DriverUpdateModal from "Pages/Modals/Driver/Update";
import VehicleLeasePurchaseApi from "Services/VehicleLeasePurchase";
import {
  createProgramServiceAgreement,
  getContractsByOwnerId,
} from "Services/Contracts/programContract";

const ENTITY_LABEL = "Programme M+";
const FILE_SAFE_ENTITY_LABEL = "ProgrammeMPlus";

const normalizeKey = (value) =>
  (value ?? "")
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toDisplayText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return value.toString();
};

const toTitleCase = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  return value
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (value) => (value ? moment(value).format("DD MMM YYYY") : "--");
const formatDateTime = (value) =>
  value ? moment(value).format("DD MMM YYYY, HH:mm") : "--";

const resolveDriverInfo = (row = {}) =>
  row?.driver || row?.driver_rides?.users || row?.driver_rides || {};

const createLeasePurchaseRow = (item = {}, index = 0, offset = 0) => {
  const driverInfo = resolveDriverInfo(item);
  const documents = [
    ...(Array.isArray(item?.documents) ? item.documents : []),
    ...(Array.isArray(item?.driver_documents) ? item.driver_documents : []),
    ...(Array.isArray(item?.driverDocuments) ? item.driverDocuments : []),
    ...(Array.isArray(item?.documents_data) ? item.documents_data : []),
    ...(Array.isArray(item?.files) ? item.files : []),
  ];
  const userId =
    item?.user_id ||
    item?.userId ||
    item?.user?.id ||
    item?.user?._id ||
    item?.driver_rides?.users?.user_id ||
    item?.driver_rides?.users?.userId ||
    item?.driver_rides?.users?._id ||
    item?.driver_rides?.users?.id ||
    item?.driver_id ||
    driverInfo?._id ||
    driverInfo?.id ||
    driverInfo?.driverId ||
    item?.driver?.id ||
    item?.driver?._id ||
    "--";
  const driverName =
    [driverInfo?.first_name, driverInfo?.last_name].filter(Boolean).join(" ") ||
    driverInfo?.name ||
    "--";

  return {
    id: item?.id || `${userId || item?.driver_id || "lease"}-${index}`,
    sno: offset + index + 1,
    leaseId: item?.id || "--",
    driverId: userId,
    userId,
    driverName,
    driverCode: driverInfo?.code || "--",
    driverEmail: driverInfo?.email || "--",
    driverPhone: driverInfo?.phone || "--",
    driverCity: driverInfo?.city || "--",
    driverCountry: driverInfo?.country || "--",
    driverAddress: driverInfo?.address || item?.driver_rides?.address || "--",
    startDate: item?.start_date || null,
    startDateLabel: formatDate(item?.start_date),
    monthsActive: item?.months_active ?? "--",
    tripAcceptanceRate: item?.trip_acceptance_rate ?? "--",
    cancellationRate: item?.cancellation_rate ?? "--",
    averageTripsPerWeek: item?.average_number_of_trips_per_week ?? "--",
    vehicleType: item?.vehicle_type || "--",
    preferredVehicleMake: item?.preferred_vehicle_make || "--",
    preferredVehicleColor: item?.preferred_vehicle_color || "--",
    repaymentPreference: item?.repayment_preference || "--",
    nationalIdNumber: item?.national_id_number || "--",
    driversLicenseNumber: item?.drivers_license_number || "--",
    driversLicenseExpirationDate: formatDate(
      item?.drivers_license_expiration_date
    ),
    criminalRecordCertificateReferenceNumber:
      item?.criminal_record_certificate_reference_number || "--",
    guarantorName: item?.guarantor_name || "--",
    guarantorRelationship: item?.guarantor_relationship || "--",
    guarantorPhoneNumber: item?.guarantor_phone_number || "--",
    guarantorIdNumber: item?.guarantor_id_number || "--",
    guarantorAddress: item?.guarantor_address || "--",
    documentCount: documents.length || item?.document_ids?.length || 0,
    status: item?.status || "PENDING",
    reviewedBy: item?.reviewed_by || "--",
    reviewedAt: item?.reviewed_at || null,
    reviewedAtLabel: formatDateTime(item?.reviewed_at),
    createdAtLabel: formatDateTime(item?.created_at),
    updatedAtLabel: formatDateTime(item?.updated_at),
    documents,
    raw: item,
  };
};

const DISABLED_BUTTON_SX = {
  backgroundColor: "#EDEFF1 !important",
  color: "#9AA1A9 !important",
  border: "1px solid #dfe3e6 !important",
  cursor: "not-allowed",
  "& .MuiSvgIcon-root": {
    color: "#9AA1A9 !important",
  },
  "&:hover": {
    backgroundColor: "#EDEFF1 !important",
    border: "1px solid #dfe3e6 !important",
    cursor: "not-allowed",
  },
};

const ContractActionButton = ({ row, contractsByOwnerId, onContractGenerated }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [working, setWorking] = useState(false);

  const status = normalizeKey(row?.status);
  const isApproved = status === "APPROVED";
  const userId = row?.userId || row?.driverId;
  const existingContract = userId ? contractsByOwnerId?.[userId] : null;
  const isDisabled = !isApproved || !userId || userId === "--" || working;

  const handleClick = async () => {
    if (isDisabled) {
      return;
    }

    if (existingContract) {
      navigate(`/contracts/${existingContract.id}`);
      return;
    }

    setWorking(true);

    try {
      const created = await createProgramServiceAgreement(
        userId,
        row?.driverEmail || "",
        row?.driverName || ""
      );
      dispatch(
        showSnackbar({
          message: "Contract generated successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );

      if (created) {
        onContractGenerated?.(userId, created);
        navigate(`/contracts/${created.id}`);
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Failed to generate contract",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <Button
      variant="contained"
      size="small"
      startIcon={existingContract ? <VisibilityOutlined /> : <NoteAddOutlined />}
      onClick={handleClick}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      sx={isDisabled ? DISABLED_BUTTON_SX : undefined}
    >
      {working ? "..." : existingContract ? "Voir le contrat" : "Générer le contrat"}
    </Button>
  );
};

const ProgrammeMPlus = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [bulkRows, setBulkRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [contractsByOwnerId, setContractsByOwnerId] = useState({});

  const fetchLeasePurchases = useCallback(async () => {
    dispatch(showLoader("Loading please wait..."));
    setLoading(true);

    try {
      const params = {
        page: page + 1,
        limit: pageSize,
      };

      const trimmedSearch = searchText.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response =
        await VehicleLeasePurchaseApi.getVehicleLeasePurchaseList(params);

      const payload = response?.data?.data ?? {};
      const leasePurchases = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      const mappedRows = leasePurchases.map((item, index) =>
        createLeasePurchaseRow(item, index, page * pageSize)
      );

      setRows(mappedRows);
      setTotalRecords(payload?.totalRecords ?? mappedRows.length);
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to fetch vehicle lease purchases",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setRows([]);
      setTotalRecords(0);
    } finally {
      dispatch(hideLoader());
      setLoading(false);
    }
  }, [dispatch, page, pageSize, searchText]);

  const fetchAllLeasePurchases = useCallback(async () => {
    try {
      const response =
        await VehicleLeasePurchaseApi.getVehicleLeasePurchaseList({
          page: 1,
          limit: "unlimited",
          search: "",
        });

      const payload = response?.data?.data ?? {};
      const leasePurchases = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      setBulkRows(leasePurchases.map((item, index) => createLeasePurchaseRow(item, index)));
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to fetch all vehicle lease purchases",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setBulkRows([]);
    }
  }, [dispatch]);

  const fetchContractsByOwner = useCallback(async () => {
    try {
      const contractsMap = await getContractsByOwnerId();
      setContractsByOwnerId(contractsMap);
    } catch (error) {
      setContractsByOwnerId({});
    }
  }, []);

  const handleContractGenerated = useCallback((ownerId, contract) => {
    setContractsByOwnerId((previous) => ({ ...previous, [ownerId]: contract }));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchLeasePurchases();
    }, 350);

    return () => window.clearTimeout(timer);
  }, [fetchLeasePurchases]);

  useEffect(() => {
    fetchContractsByOwner();
  }, [fetchContractsByOwner]);

  useEffect(() => {
    fetchAllLeasePurchases();
  }, [fetchAllLeasePurchases]);

  const handleRefresh = useCallback(() => {
    fetchLeasePurchases();
    fetchAllLeasePurchases();
    fetchContractsByOwner();
  }, [fetchAllLeasePurchases, fetchContractsByOwner, fetchLeasePurchases]);

  const handleOpenDetails = useCallback((row) => {
    setSelectedRow(row);
    setDetailsOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedRow(null);
    fetchLeasePurchases();
    fetchContractsByOwner();
  }, [fetchContractsByOwner, fetchLeasePurchases]);

  const getExportRows = useCallback(
    (inputRows = []) =>
      inputRows.map((row) => ({
        "S.NO": row?.sno,
        Driver: row?.driverName,
        "Driver Code": row?.driverCode,
        "Driver Email": row?.driverEmail,
        "Driver Phone": row?.driverPhone,
        "Start Date": row?.startDateLabel,
        "Months Active": row?.monthsActive,
        "Vehicle Type": row?.vehicleType,
        "Acceptance Rate": `${toDisplayText(row?.tripAcceptanceRate)}%`,
        "Cancellation Rate": `${toDisplayText(row?.cancellationRate)}%`,
        "Trips / Week": row?.averageTripsPerWeek,
        Documents: row?.documentCount,
        Status: toTitleCase(row?.status),
      })),
    []
  );

  const currentExportRows = useMemo(() => getExportRows(rows), [getExportRows, rows]);
  const allExportRows = useMemo(() => getExportRows(bulkRows), [bulkRows, getExportRows]);

  const handleCopy = useCallback(() => {
    const csv = Papa.unparse(currentExportRows);
    navigator.clipboard.writeText(csv).then(() => {
      dispatch(
        showSnackbar({
          message: "Table copied to clipboard",
          autoHideDuration: 2000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
    });
  }, [currentExportRows, dispatch]);

  const handleDownloadExcel = useCallback(() => {
    const worksheet = XLSX.utils.json_to_sheet(currentExportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, ENTITY_LABEL);
    XLSX.writeFile(workbook, `${FILE_SAFE_ENTITY_LABEL}List.xlsx`);
  }, [currentExportRows]);

  const handleDownloadCSV = useCallback(() => {
    const csv = Papa.unparse(currentExportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${FILE_SAFE_ENTITY_LABEL}List.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [currentExportRows]);

  const handleDownloadPDF = useCallback(() => {
    const doc = new jsPDF("landscape");
    const headers = Object.keys(currentExportRows[0] || {
      "S.NO": "",
      Driver: "",
      "Driver Code": "",
      "Driver Email": "",
      "Driver Phone": "",
      "Start Date": "",
      "Months Active": "",
      "Vehicle Type": "",
      "Acceptance Rate": "",
      "Cancellation Rate": "",
      "Trips / Week": "",
      Documents: "",
      Status: "",
    });

    const body = currentExportRows.map((row) => headers.map((header) => row[header]));

    doc.autoTable({
      head: [headers],
      body,
      styles: {
        fontSize: 7,
      },
    });
    doc.save(`${FILE_SAFE_ENTITY_LABEL}List.pdf`);
  }, [currentExportRows]);

  const handleDownloadAll = useCallback(() => {
    const csv = Papa.unparse(allExportRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${FILE_SAFE_ENTITY_LABEL}AllList.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [allExportRows]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.4,
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <div className={classes.lightText}>
            <Typography variant="body2">{params?.row?.sno}</Typography>
          </div>
        ),
      },
      {
        field: "driverName",
        headerName: "Driver",
        flex: 1.4,
        minWidth: 220,
        sortable: true,
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Box className={classes.driverCell}>
              <span className={classes.driverPrimary}>{row?.driverName}</span>
              <span className={classes.driverSecondary}>
                {row?.driverCode}{" "}
                {row?.driverPhone !== "--" ? `- ${row?.driverPhone}` : ""}
              </span>
              <span className={classes.driverSecondary}>{row?.driverEmail}</span>
            </Box>
          );
        },
      },
      {
        field: "startDateLabel",
        headerName: "Start Date",
        flex: 0.8,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => params?.row?.startDateLabel || "--",
      },
      {
        field: "monthsActive",
        headerName: "Months",
        flex: 0.5,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        sortable: false,
      },
      {
        field: "vehicleType",
        headerName: "Vehicle",
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Box className={classes.driverCell}>
              <span className={classes.driverPrimary}>
                {toTitleCase(row?.vehicleType)}
              </span>
              <span className={classes.driverSecondary}>
                {toDisplayText(row?.preferredVehicleMake)} /{" "}
                {toDisplayText(row?.preferredVehicleColor)}
              </span>
            </Box>
          );
        },
      },
      {
        field: "tripAcceptanceRate",
        headerName: "Acceptance",
        flex: 0.7,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => `${toDisplayText(params?.row?.tripAcceptanceRate)}%`,
      },
      {
        field: "cancellationRate",
        headerName: "Cancellation",
        flex: 0.7,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => `${toDisplayText(params?.row?.cancellationRate)}%`,
      },
      {
        field: "averageTripsPerWeek",
        headerName: "Trips / Week",
        flex: 0.7,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
      },
      {
        field: "documentCount",
        headerName: "Documents",
        flex: 0.7,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={`${params?.row?.documentCount || 0}`}
            size="small"
            className={classes.summaryChip}
            sx={{
              backgroundColor: "#EEF2FF",
              color: "#4338CA",
            }}
          />
        ),
      },
      {
        field: "contract",
        headerName: "Contract",
        flex: 0.9,
        minWidth: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <ContractActionButton
            row={params?.row}
            contractsByOwnerId={contractsByOwnerId}
            onContractGenerated={handleContractGenerated}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Documents / Service Type",
        flex: 1.6,
        minWidth: 240,
        resizable: true,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          (() => {
            const rowUserId = params?.row?.userId || params?.row?.driverId;
            const rowStatus = (params?.row?.status || "PENDING").toString().toUpperCase();
            const isApproved = ["APPROVED", "VALIDATED", "ACTIVE", "COMPLETED", "ENABLED"].includes(
              rowStatus
            );
            const isRejected = [
              "BANNED",
              "REJECTED",
              "DECLINED",
              "FAILED",
              "DISABLED",
              "CANCELLED",
            ].includes(rowStatus);
            const statusLabel = rowStatus || "PENDING";
            const statusBg = isApproved ? "#4CAF50" : isRejected ? "#E53935" : "#FB8C00";
            const statusIcon = isApproved ? (
              <CheckBox />
            ) : isRejected ? (
              <NotInterested />
            ) : (
              <Fingerprint />
            );

            return (
              <div className={classes.iconsWrapper}>
                <section className={classes.editIcon}>
                  <Button
                    variant="contained"
                    startIcon={statusIcon}
                    onClick={() => handleOpenDetails(params?.row)}
                    disabled={!rowUserId || rowUserId === "--"}
                    sx={{
                      backgroundColor: `${statusBg} !important`,
                      color: "#fff !important",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.9,
                      minWidth: 160,
                      fontWeight: 700,
                      boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
                      "& .MuiSvgIcon-root": { color: "#fff !important" },
                      "&:hover": { backgroundColor: `${statusBg} !important`, opacity: 0.95 },
                    }}
                  >
                    {statusLabel}
                  </Button>
                </section>
              </div>
            );
          })()
        ),
      },
    ],
    [classes, contractsByOwnerId, handleContractGenerated, handleOpenDetails]
  );

  function CustomToolbar() {
    return (
      <Box
        sx={{
          p: 1,
          pb: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const NoRowsOverlay = () => (
    <Box className={classes.emptyState}>
      <Typography variant="body2">
        No vehicle lease purchase records found for the current filters.
      </Typography>
    </Box>
  );

  const selectedDriverId =
    (selectedRow?.userId && selectedRow?.userId !== "--" && selectedRow?.userId) ||
    (selectedRow?.driverId && selectedRow?.driverId !== "--" && selectedRow?.driverId) ||
    selectedRow?.raw?.user_id ||
    selectedRow?.raw?.userId ||
    selectedRow?.raw?.driver_id ||
    selectedRow?.raw?.driverId ||
    selectedRow?.raw?.driver?.id ||
    selectedRow?.raw?.driver?._id ||
    selectedRow?.raw?.driver_rides?.users?._id ||
    selectedRow?.raw?.driver_rides?.users?.id ||
    selectedRow?.id ||
    "";

  return (
    <>
      <Box className={classes.root}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>
              COPY
            </Button>
            <Button
              variant="contained"
              startIcon={<TableChartOutlined />}
              onClick={handleDownloadExcel}
            >
              EXCEL
            </Button>
            <Button
              variant="contained"
              startIcon={<DescriptionOutlined />}
              onClick={handleDownloadCSV}
            >
              CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfOutlined />}
              onClick={handleDownloadPDF}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlined />}
              onClick={handleDownloadAll}
            >
              Download ALL
            </Button>
          </Box>

          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>

        <Box className={classes.tableWrapper}>
          <DataGrid
            className={classes.dataGridTable}
            rows={rows}
            columns={columns}
            page={page}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            loading={loading}
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={92}
            headerHeight={58}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalRecords}
            filterMode="server"
            onFilterModelChange={(model) => {
              const value = model?.quickFilterValues?.join(" ") || "";
              setSearchText(value);
              setPage(0);
            }}
            density="standard"
            sx={{ width: "100%" }}
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay,
            }}
            getRowId={(row) => row?.id}
          />
        </Box>
      </Box>

      <DriverUpdateModal
        key={selectedDriverId || "programme-mplus-driver-details"}
        openDialog={detailsOpen}
        handleClose={handleCloseDetails}
        driverId={selectedDriverId}
        entityLabel="Driver"
        showBankInformation={false}
        showMPlusInformation
        documentsTabLabel="Documents"
        documentsUserType="PROGRAM"
        statusVariant="PROGRAM"
      />
    </>
  );
};

export default ProgrammeMPlus;
